import { useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import { Chevron } from '../Chevron';
import { ConfirmDialog } from '../ConfirmDialog';
import {
  useCodeRepos,
  useCodeRules,
  useRepoCode,
  useRuleChange,
} from '../../hooks/useCodeRules';
import type {
  CodeRepo,
  CodeRule,
  RuleBody,
  RuleChange,
  RulePreview,
  RuleSources,
} from '../../types/codeRules';
import { RefreshError } from '../RefreshError';
import { FacetSearch } from './FacetSearch';

const GROUP_PREFIX = 'code-access-';
// Table rows besides the specific rules, which are keyed by rule pk.
const DEFAULT_ROW = 'default';
const NO_RULE = 'no-rule';
const UNCLASSIFIED = 'unclassified';
const RUNS_HINT = 'Eval sets and scans, imported ones included';
const TITLE = {
  create: 'New rule',
  update: 'Change rule',
  delete: 'Delete rule',
};

interface Draft {
  pk: string | null;
  url_prefix: string;
  group: string;
}

function GroupName({ group }: { group: string | null }) {
  return group === null ? (
    <span
      className="italic text-muted"
      title="No rule matches, so the source adds no restriction."
    >
      unrestricted
    </span>
  ) : (
    <span className="font-mono text-ink">{group}</span>
  );
}

/** Code-access rules, previewed before saving, and the sources they classify. */
export function CodeAccessTab() {
  return (
    <div className="p-4 text-xs text-ink-2">
      <Rules />
    </div>
  );
}

const sourceKind = (repo: CodeRepo) =>
  repo.kind === 'package_index' ? 'package' : repo.kind;
const sourceRow = (row: RuleSources) =>
  row.valid ? (row.rule_pk ?? NO_RULE) : UNCLASSIFIED;
const sourceText = (repo: CodeRepo) => repo.canonical_url;

function Rules() {
  const rules = useCodeRules();
  const repos = useCodeRepos();
  // A failed refetch, such as after a save, keeps the last rows; say so.
  const stale = [rules, repos].filter(q => q.error && q.data);
  const sources = useMemo(
    () => repos.data && new Map(repos.data.map(row => [sourceRow(row), row])),
    [repos.data]
  );
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (row: string) => setOpen(open === row ? null : row);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pending, setPending] = useState<{
    change: RuleChange;
    preview: RulePreview;
  } | null>(null);
  const saved = useCallback(() => {
    setPending(null);
    setDraft(null);
  }, []);
  const { busy, error, preview, save, reset } = useRuleChange(saved);
  const formId = useId();

  const review = async (change: RuleChange) => {
    const result = await preview(change);
    if (result) setPending({ change, preview: result });
  };
  // `*` has its own row, shown even before it exists.
  const fallback = rules.data?.find(rule => rule.url_prefix === '*');
  // Each row's sources and runs come from one registry pass.
  const usage = (key: string) => {
    const row = sources?.get(key);
    return {
      count: row?.sources.length ?? 0,
      runs: sources && (row?.runs ?? 0),
      repos: sources && (row?.sources ?? []),
    };
  };
  const remove = (rule: CodeRule) => {
    edit(null);
    void review({ action: 'delete', pk: rule.pk, rule });
  };
  const edit = (next: Draft | null) => {
    reset();
    setDraft(next);
  };

  return (
    <section>
      <div className="flex items-center gap-3">
        <h2 className="eyebrow">
          Rules:
          {rules.data && <b className="ml-1.5 text-ink">{rules.data.length}</b>}
        </h2>
        <button
          type="button"
          className="row-action row-action-accent"
          onClick={() =>
            edit({ pk: null, url_prefix: '', group: GROUP_PREFIX })
          }
        >
          New rule
        </button>
      </div>
      <p className="mt-1 text-muted">
        A source needs the group of its longest matching prefix, or the default
        group when nothing else matches. With no default group, a source no rule
        matches adds no restriction.
      </p>

      {draft && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={event => {
            event.preventDefault();
            const rule = { url_prefix: draft.url_prefix, group: draft.group };
            void review(
              draft.pk === null
                ? { action: 'create', rule }
                : { action: 'update', pk: draft.pk, rule }
            );
          }}
        >
          <div className="flex flex-col gap-1">
            <label htmlFor={`${formId}-prefix`} className="text-muted">
              Source prefix
            </label>
            <input
              id={`${formId}-prefix`}
              className="input h-7 w-72 font-mono text-xs"
              required
              value={draft.url_prefix}
              placeholder="github.com/org/repo or a package"
              onChange={e => setDraft({ ...draft, url_prefix: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`${formId}-group`} className="text-muted">
              Required group
            </label>
            <input
              id={`${formId}-group`}
              className="input h-7 w-56 font-mono text-xs"
              required
              value={draft.group}
              onChange={e => setDraft({ ...draft, group: e.target.value })}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Previewing…' : 'Preview…'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => edit(null)}
          >
            Cancel
          </button>
        </form>
      )}
      {error && !pending && (
        <p className="callout callout-err mt-3 max-w-3xl">{error}</p>
      )}

      {rules.error && !rules.data ? (
        <p className="mt-3 text-err">{rules.error.message}</p>
      ) : !rules.data ? (
        <p className="mt-3 text-muted">Loading rules…</p>
      ) : (
        <>
          <RefreshError
            error={stale[0]?.error ?? null}
            onRetry={() => stale.forEach(q => void q.refetch())}
          />
          {rules.data.length === 0 && (
            <p className="mt-3 text-muted">
              No rules yet, so no source is restricted.
            </p>
          )}
          <table className="mt-3 w-full max-w-5xl text-left">
            <thead className="eyebrow">
              <tr>
                <th className="py-1.5 pr-4 font-semibold">Prefix</th>
                <th className="px-2 font-semibold">Group</th>
                <th className="px-2 text-right font-semibold">Sources</th>
                <th className="px-2 text-right font-semibold" title={RUNS_HINT}>
                  Runs
                </th>
                <th className="px-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <SourceGroup
                label={
                  <span className="text-ink">
                    Default <code className="text-muted">*</code>
                  </span>
                }
                group={
                  <DefaultGroup
                    key={fallback?.group ?? ''}
                    current={fallback?.group}
                    busy={busy}
                    onSet={group => {
                      edit(null);
                      const rule = { url_prefix: '*', group };
                      void review(
                        fallback
                          ? { action: 'update', pk: fallback.pk, rule }
                          : { action: 'create', rule }
                      );
                    }}
                  />
                }
                {...usage(fallback?.pk ?? NO_RULE)}
                open={open === DEFAULT_ROW}
                onToggle={() => toggle(DEFAULT_ROW)}
              >
                {fallback && (
                  <button
                    type="button"
                    className="row-action row-action-danger"
                    disabled={busy}
                    onClick={() => remove(fallback)}
                  >
                    Remove
                  </button>
                )}
              </SourceGroup>
              {rules.data
                .filter(rule => rule !== fallback)
                .map(rule => (
                  <SourceGroup
                    key={rule.pk}
                    label={
                      <span className="font-mono text-ink">
                        {rule.url_prefix}
                      </span>
                    }
                    group={<GroupName group={rule.group} />}
                    {...usage(rule.pk)}
                    open={open === rule.pk}
                    onToggle={() => toggle(rule.pk)}
                  >
                    <button
                      type="button"
                      className="row-action"
                      disabled={busy}
                      onClick={() =>
                        edit({
                          pk: rule.pk,
                          url_prefix: rule.url_prefix,
                          group: rule.group,
                        })
                      }
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="row-action row-action-danger ml-3"
                      disabled={busy}
                      onClick={() => remove(rule)}
                    >
                      Delete
                    </button>
                  </SourceGroup>
                ))}
              {sources?.has(UNCLASSIFIED) && (
                <SourceGroup
                  label={<span className="text-warn">Can’t classify</span>}
                  group={<span className="text-warn">unresolved</span>}
                  {...usage(UNCLASSIFIED)}
                  note={
                    <p className="mt-1 max-w-3xl text-muted">
                      An older deployment recorded these as written, so no rule
                      can match them. Runs that use them stay hidden from
                      non-admins until relaunched under a new ID.
                    </p>
                  }
                  open={open === UNCLASSIFIED}
                  onToggle={() => toggle(UNCLASSIFIED)}
                />
              )}
            </tbody>
          </table>
          {repos.error && !repos.data && (
            <p className="mt-2 text-err">
              Couldn’t load sources: {repos.error.message}
            </p>
          )}
        </>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={
          pending && (
            <>
              {TITLE[pending.change.action]}{' '}
              <span className="font-mono text-xs font-normal text-ink-2">
                {pending.change.rule.url_prefix}
              </span>
            </>
          )
        }
        confirmLabel={
          pending?.change.action === 'delete' ? 'Delete rule' : 'Save rule'
        }
        busyLabel="Saving…"
        destructive={pending?.change.action === 'delete'}
        busy={busy}
        error={pending ? error : null}
        onConfirm={() => {
          if (!pending) return;
          // Deleting loosens access, so it asks twice, like terminating a sample.
          const { change, preview } = pending;
          if (
            change.action === 'delete' &&
            !window.confirm(deleteQuestion(change.rule, preview))
          )
            return;
          void save(change);
        }}
        onClose={() => {
          setPending(null);
          reset();
        }}
      >
        {pending && <PreviewSummary {...pending} />}
      </ConfirmDialog>
    </section>
  );
}

/** A delete's second question, asked in the browser's own dialog. */
function deleteQuestion(rule: RuleBody, preview: RulePreview) {
  const counts = `Sources changing group: ${preview.source_count.toLocaleString()}. Runs using them: ${preview.run_count.toLocaleString()}.`;
  return rule.url_prefix === '*'
    ? `Remove the default group ${rule.group}? The sources it covers become unrestricted. ${counts}`
    : `Delete the rule for ${rule.url_prefix}? Its sources fall back to a broader rule or to no restriction. ${counts}`;
}

function PreviewSummary({
  change,
  preview,
}: {
  change: RuleChange;
  preview: RulePreview;
}) {
  const { rule } = change;
  return (
    <>
      <p>
        Sources under <code className="text-ink">{rule.url_prefix}</code>{' '}
        {change.action === 'delete' ? (
          'fall back to a broader rule, or to no restriction.'
        ) : (
          <>
            will need <code className="text-ink">{rule.group}</code>.
          </>
        )}
      </p>
      {change.action !== 'delete' && rule.url_prefix === '*' && (
        <p className="callout callout-warn mt-2">
          <code>*</code> covers every source no other rule matches, including
          imported runs whose code is unknown.
        </p>
      )}
      {preview.new_group && (
        <p className="callout callout-warn mt-2">
          <code>{rule.group}</code> is new to Hawk. Only people your identity
          provider grants it can use these sources, admins included, so check
          who has it before you save.
        </p>
      )}
      {preview.source_count === 0 ? (
        <p className="mt-3">No recorded source changes group.</p>
      ) : preview.source_count > preview.repos.length ? (
        <>
          <p className="mt-3">
            <b>{preview.source_count.toLocaleString()}</b> recorded sources
            change group:
          </p>
          <table className="mt-1 w-full text-left">
            <thead className="eyebrow">
              <tr>
                <th className="py-1 pr-3 font-semibold">Before</th>
                <th className="px-2 font-semibold">After</th>
                <th className="px-2 text-right font-semibold">Sources</th>
              </tr>
            </thead>
            <tbody>
              {preview.changes.map(change => (
                <tr
                  key={`${change.before}>${change.after}`}
                  className="border-t border-line"
                >
                  <td className="py-1 pr-3">
                    <GroupName group={change.before} />
                  </td>
                  <td className="px-2">
                    <GroupName group={change.after} />
                  </td>
                  <td className="px-2 text-right tabular-nums">
                    {change.sources.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <table className="mt-3 w-full text-left">
          <thead className="eyebrow">
            <tr>
              <th className="py-1 pr-3 font-semibold">Source</th>
              <th className="px-2 font-semibold">Before</th>
              <th className="px-2 font-semibold">After</th>
            </tr>
          </thead>
          <tbody>
            {preview.repos.map(repo => (
              <tr key={repo.canonical_url} className="border-t border-line">
                <td className="break-all py-1 pr-3 font-mono text-ink">
                  {repo.canonical_url}
                </td>
                <td className="px-2">
                  <GroupName group={repo.before} />
                </td>
                <td className="px-2">
                  <GroupName group={repo.after} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-3">
        Recorded runs using these sources:{' '}
        <b>{preview.run_count.toLocaleString()}</b>. Saving updates what they
        require, and what scans that read them require.
      </p>
    </>
  );
}

/** The `*` rule's group: fill it in to require it of every otherwise unmatched source. */
function DefaultGroup({
  current,
  busy,
  onSet,
}: {
  current: string | undefined;
  busy: boolean;
  onSet: (group: string) => void;
}) {
  const [group, setGroup] = useState(current ?? GROUP_PREFIX);
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={event => {
        event.preventDefault();
        onSet(group);
      }}
    >
      <input
        aria-label="Default group"
        className="input h-7 w-56 font-mono text-xs"
        required
        value={group}
        onChange={e => setGroup(e.target.value)}
      />
      <button
        type="submit"
        className="row-action row-action-accent"
        disabled={busy || group === current}
      >
        Set…
      </button>
      {current === undefined && (
        <span className="text-muted">not set: no restriction</span>
      )}
    </form>
  );
}

/** A rule, or the sources no rule governs, opening onto its sources. */
function SourceGroup({
  label,
  group,
  count,
  runs,
  repos,
  note,
  open,
  onToggle,
  children,
}: {
  label: ReactNode;
  group: ReactNode;
  count: number;
  runs?: number;
  /** Undefined while sources load. */
  repos: CodeRepo[] | undefined;
  note?: ReactNode;
  open: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  return (
    <>
      <tr className="border-t border-line align-middle">
        <td className="break-all py-1.5 pr-4">
          {count > 0 ? (
            <button
              type="button"
              className="row-action inline-flex items-center gap-1.5 text-left"
              aria-expanded={open}
              onClick={onToggle}
            >
              <Chevron open={open} size={16} />
              {label}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 shrink-0" />
              {label}
            </span>
          )}
        </td>
        <td className="px-2">{group}</td>
        <td className="px-2 text-right tabular-nums">
          {count.toLocaleString()}
        </td>
        <td className="px-2 text-right tabular-nums">
          {runs?.toLocaleString()}
        </td>
        <td className="whitespace-nowrap px-2 text-right">{children}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="pb-3 pl-4">
            {repos ? (
              <>
                {note}
                <SourceList repos={repos} />
              </>
            ) : (
              <p className="text-muted">Loading sources…</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function SourceList({ repos }: { repos: CodeRepo[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <FacetSearch
      title="Sources"
      noun="source"
      items={repos}
      facet={sourceKind}
      text={sourceText}
    >
      {matches => (
        <table className="mt-2 w-full text-left">
          <thead className="eyebrow">
            <tr>
              <th className="py-1.5 pr-4 font-semibold">Source</th>
              <th className="px-2 font-semibold">Kind</th>
              <th className="px-2 text-right font-semibold" title={RUNS_HINT}>
                Runs
              </th>
              <th className="px-2 text-right font-semibold">Code items</th>
            </tr>
          </thead>
          <tbody>
            {matches.map(repo => (
              <SourceRow
                key={repo.pk}
                repo={repo}
                open={open === repo.pk}
                onToggle={() => setOpen(open === repo.pk ? null : repo.pk)}
              />
            ))}
          </tbody>
        </table>
      )}
    </FacetSearch>
  );
}

function SourceRow({
  repo,
  open,
  onToggle,
}: {
  repo: CodeRepo;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-t border-line align-middle">
        <td className="py-1.5 pr-4">
          {repo.code_items > 0 ? (
            <button
              type="button"
              className="row-action break-all text-left font-mono text-ink"
              aria-expanded={open}
              onClick={onToggle}
            >
              {repo.canonical_url}
            </button>
          ) : (
            <span className="break-all font-mono text-ink">
              {repo.canonical_url}
            </span>
          )}
        </td>
        <td className="px-2 text-muted">{sourceKind(repo)}</td>
        <td className="px-2 text-right tabular-nums">
          {repo.runs.toLocaleString()}
        </td>
        <td className="px-2 text-right tabular-nums">{repo.code_items}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} className="pb-2 pl-4">
            <RepoCode pk={repo.pk} />
          </td>
        </tr>
      )}
    </>
  );
}

function RepoCode({ pk }: { pk: string }) {
  const items = useRepoCode(pk);
  if (items.error && !items.data)
    return <p className="text-err">{items.error.message}</p>;
  if (!items.data) return <p className="text-muted">Loading code items…</p>;
  return (
    <ul className="space-y-0.5">
      {items.data.map(item => (
        <li
          key={`${item.item_type}:${item.subdirectory ?? ''}:${item.registry_name}`}
          className="grid grid-cols-[5rem_minmax(0,1fr)] items-baseline gap-x-2"
        >
          <span className="badge justify-self-start">{item.item_type}</span>
          <span className="break-words">
            <span className="font-mono text-ink">{item.registry_name}</span>
            {item.subdirectory && (
              <span className="text-muted"> in {item.subdirectory}</span>
            )}
            {item.ref && <span className="text-muted"> @ {item.ref}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}
